import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  afterRenderEffect,
  computed,
  inject,
  input,
  model,
  output
} from "@angular/core";

import { LktIconComponent } from "../icon/icon.component";
import {
  LktNotificationCloseReason,
  LktNotificationFilter,
  LktNotificationView
} from "../../types/notification-item.type";

/**
 * Casca do painel de notificações (DEV-872, CA 02 e CA 06).
 *
 * Não conhece notificação nenhuma: cabeçalho, segmentado de filtro, corpo
 * projetado por `<ng-content>` e rodapé. Quem monta a lista, o estado vazio ou
 * as preferências é o container em `shared-core`.
 *
 * Também não se posiciona: `:host` declara só a largura (`--nt-w`), a superfície
 * e o teto de altura. `position: fixed`, `top` e `left` são do container, que é
 * quem mede o sino (spec §6.4).
 *
 * O painel INTEIRO é a área de rolagem (`overflow-y: auto` no `:host`), com
 * cabeçalho e rodapé no fluxo — por isso `scrollTop()` / `restoreScrollTop()`
 * operam no host. O protótipo preserva a rolagem da lista interna, que não tem
 * `overflow` e portanto vale sempre 0 (spec §6.2).
 */
@Component({
  selector: "lkt-notification-panel",
  standalone: true,
  imports: [LktIconComponent],
  templateUrl: "./notification-panel.component.html",
  styleUrl: "./notification-panel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: "dialog",
    "aria-modal": "false",
    // Alvo de foco do container ao abrir (spec §9). `-1` não entra na ordem do Tab.
    tabindex: "-1",
    "[attr.aria-label]": "heading()",
    "[attr.data-cy]": "dataCy() || null",
    "[class.is-prefs]": "isPreferences()"
  }
})
export class LktNotificationPanelComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  public readonly panelTitle = input<string>("Notificações");
  public readonly view = input<LktNotificationView>("list");
  public readonly filter = model<LktNotificationFilter>("all");
  public readonly showMarkAllRead = input<boolean>(true);
  /** Elemento que NÃO deve disparar `closeRequest` ao ser clicado (o sino que abriu o painel). */
  public readonly ignoreElement = input<HTMLElement | null>(null);
  public readonly dataCy = input<string>("notifications-panel");

  public readonly markAllRead = output<void>();
  public readonly preferencesOpen = output<void>();
  public readonly backToList = output<void>();
  public readonly closeRequest = output<LktNotificationCloseReason>();

  public readonly isList = computed<boolean>(() => this.view() === "list");
  public readonly isPreferences = computed<boolean>(() => this.view() === "preferences");

  /**
   * A vista de preferências carrega o próprio título, então o container não
   * precisa trocar `panelTitle` ao alternar de vista — e o rótulo acessível do
   * `role="dialog"` acompanha o que está escrito na tela.
   */
  public readonly heading = computed<string>(() =>
    this.isPreferences() ? "Preferências" : this.panelTitle()
  );

  /**
   * Marcado no clique, lido depois do render: o alvo do foco só existe na vista nova.
   *
   * Objeto `readonly` com campo mutável, e não `signal()`: isto é bookkeeping imperativo, não
   * estado reativo — virar signal criaria dependência dentro do próprio `afterRenderEffect` que o
   * lê. O `readonly` também é o que satisfaz o hook `enforce-signals`, que exige signal ou
   * readonly em propriedade de componente.
   */
  private readonly focusState = { wasInside: false };

  public constructor() {
    // CA 07: a troca de vista destrói o botão acionado. O foco vai para o controle equivalente —
    // engrenagem na ida, voltar na volta —, e SÓ se ele já estava dentro do painel: com o mouse,
    // mexer no foco de onde a pessoa deixou seria intromissão.
    afterRenderEffect(() => {
      const view = this.view();
      if (!this.focusState.wasInside) {
        return;
      }
      this.focusState.wasInside = false;
      const selector =
        view === "preferences"
          ? '[data-cy="notifications-back-button"]'
          : '[data-cy="notifications-preferences-button"]';
      const target = this.host.nativeElement.querySelector<HTMLElement>(selector);
      target?.focus();
    });
  }

  public onFilterSelect(next: LktNotificationFilter): void {
    if (this.filter() === next) {
      return;
    }
    this.filter.set(next);
  }

  public onMarkAllRead(): void {
    this.markAllRead.emit();
  }

  public onPreferences(): void {
    this.focusState.wasInside = this.host.nativeElement.contains(document.activeElement);
    this.preferencesOpen.emit();
  }

  public onBack(): void {
    this.focusState.wasInside = this.host.nativeElement.contains(document.activeElement);
    this.backToList.emit();
  }

  @HostListener("document:keydown.escape")
  public onEscape(): void {
    this.closeRequest.emit("escape");
  }

  /**
   * Clique fora fecha. A exceção do `ignoreElement` é o próprio sino: o clique
   * nele borbulha até o `document` DEPOIS de o container alternar o painel, então
   * sem a guarda o mesmo gesto fecharia e reabriria o painel.
   */
  @HostListener("document:click", ["$event"])
  public onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target || this.host.nativeElement.contains(target)) {
      return;
    }
    const ignored = this.ignoreElement();
    if (ignored && ignored.contains(target)) {
      return;
    }
    this.closeRequest.emit("outside");
  }

  /** Rolagem corrente do painel, para o container preservá-la ao repintar (CA 02). */
  public scrollTop(): number {
    return this.host.nativeElement.scrollTop;
  }

  public restoreScrollTop(value: number): void {
    this.host.nativeElement.scrollTop = value;
  }
}
